import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  AlertTriangle,
  FlaskConical,
} from 'lucide-react';
import API_URL from '../../config';
import { PageHeader } from '../ui';
import '../../App.css';
import './clearinghouse-page.css';

const FMCSA_CLEARINGHOUSE = 'https://clearinghouse.fmcsa.dot.gov';

function formatLongDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function toDateInputValue(dateValue) {
  if (!dateValue) return '';
  const isoDay = String(dateValue).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return isoDay;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isLocalCalendarDate(dateValue) {
  if (!dateValue) return false;
  const stored = toDateInputValue(dateValue);
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return stored === `${y}-${m}-${d}`;
}

const ACCORDION_SECTIONS = [
  {
    id: 'what',
    title: 'What is the Clearinghouse?',
    content: (
      <>
        <p>
          The FMCSA Drug & Alcohol Clearinghouse is a secure online database that tracks drug and alcohol
          violations by commercial driver&apos;s license (CDL) holders. It was established to improve highway
          safety by ensuring that drivers with drug and alcohol violations are not operating commercial motor
          vehicles.
        </p>
        <p>
          The Clearinghouse provides employers and authorized users with real-time information about CDL
          drivers&apos; drug and alcohol program violations, making it easier to identify drivers who are
          prohibited from operating commercial vehicles.
        </p>
      </>
    ),
  },
  {
    id: 'who',
    title: 'Who is required to register?',
    content: (
      <>
        <p>
          <strong>All CDL drivers must register</strong> in the FMCSA Clearinghouse. This includes:
        </p>
        <ul>
          <li>Commercial driver&apos;s license (CDL) holders</li>
          <li>Commercial learner&apos;s permit (CLP) holders</li>
          <li>Owner-operators who drive</li>
          <li>Any driver operating a commercial motor vehicle (CMV)</li>
        </ul>
        <p>
          Registration is <strong>mandatory</strong> and required by federal law. You cannot operate a commercial
          vehicle without being registered in the Clearinghouse.
        </p>
      </>
    ),
  },
  {
    id: 'register',
    title: 'How to register — step by step',
    content: (
      <>
        <p>Follow these steps to register in the Clearinghouse:</p>
        <ol>
          <li>
            <strong>Visit the Clearinghouse website:</strong>{' '}
            <a href={FMCSA_CLEARINGHOUSE} target="_blank" rel="noopener noreferrer" className="clearinghouse-page__inline-link">
              clearinghouse.fmcsa.dot.gov
            </a>
          </li>
          <li>
            <strong>Click &quot;Register&quot;</strong> and select &quot;Driver&quot; as your role
          </li>
          <li>
            <strong>Provide your information:</strong>
            <ul>
              <li>Full legal name</li>
              <li>Date of birth</li>
              <li>CDL number and state</li>
              <li>Social Security Number (SSN)</li>
              <li>Email address</li>
              <li>Phone number</li>
            </ul>
          </li>
          <li>
            <strong>Create a username and password</strong> for your account
          </li>
          <li>
            <strong>Verify your email address</strong> by clicking the link sent to your email
          </li>
          <li>
            <strong>Complete identity verification</strong> (may require additional documentation)
          </li>
        </ol>
        <p>
          <a href={FMCSA_CLEARINGHOUSE} target="_blank" rel="noopener noreferrer" className="clearinghouse-page__inline-link">
            Open FMCSA Clearinghouse to register
          </a>
        </p>
      </>
    ),
  },
  {
    id: 'consent',
    title: 'Understanding consent',
    content: (
      <>
        <p>
          <strong>Consent</strong> is your authorization for an employer (or prospective employer) to query your
          Clearinghouse record. Employers are required to query the Clearinghouse before hiring a driver and
          annually for current employees.
        </p>
        <p>When you provide consent, you are allowing the employer to:</p>
        <ul>
          <li>Run a pre-employment query (full query) before hiring</li>
          <li>Run an annual limited query to check for new violations</li>
          <li>View your violation history (if any)</li>
        </ul>
        <p>
          <strong>Consent is valid for 1 year</strong> from the date you provide it. After expiration, you must
          provide new consent for the employer to continue querying your record.
        </p>
        <p>
          It&apos;s important to track your consent expiration date and provide new consent before it expires to
          avoid delays in employment or operations.
        </p>
        <p>
          <strong>For owner-operators with employees:</strong> you have additional responsibilities: run
          pre-employment queries on drivers before hiring, run annual limited queries on current employees at least
          once every 365 days, maintain records of queries, and report violations to the Clearinghouse as required.
          Use the Query Tracker under this app if you perform employer queries.
        </p>
      </>
    ),
  },
  {
    id: 'consequences',
    title: 'Consequences of non-registration',
    content: (
      <>
        <p>
          <strong>You cannot legally operate a commercial motor vehicle</strong> without being registered in the
          Clearinghouse. Consequences include:
        </p>
        <ul>
          <li>Employers cannot hire you without a Clearinghouse query</li>
          <li>You may face fines and penalties</li>
          <li>Your CDL may be suspended or revoked</li>
          <li>You may be prohibited from operating commercial vehicles</li>
          <li>Violations are permanently recorded once registered</li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq',
    title: 'Frequently asked questions',
    content: (
      <>
        <div className="clearinghouse-page__faq-block">
          <h4 className="clearinghouse-page__faq-q">Do I need to register if I&apos;m an owner-operator?</h4>
          <p>
            Yes, if you drive a commercial vehicle, you must register as a driver. If you also have employees,
            you&apos;ll need a separate employer account.
          </p>
        </div>
        <div className="clearinghouse-page__faq-block">
          <h4 className="clearinghouse-page__faq-q">How much does registration cost?</h4>
          <p>Registration is free for drivers. There is no cost to create and maintain your Clearinghouse account.</p>
        </div>
        <div className="clearinghouse-page__faq-block">
          <h4 className="clearinghouse-page__faq-q">What if I have a violation in my record?</h4>
          <p>
            Violations remain in the Clearinghouse permanently. You must complete the required return-to-duty
            process before you can operate a commercial vehicle again.
          </p>
        </div>
        <div className="clearinghouse-page__faq-block">
          <h4 className="clearinghouse-page__faq-q">Can I see my own record?</h4>
          <p>Yes, once registered, you can log in to view your own Clearinghouse record at any time.</p>
        </div>
        <div className="clearinghouse-page__faq-block">
          <h4 className="clearinghouse-page__faq-q">What information is in the Clearinghouse?</h4>
          <p>
            The Clearinghouse contains information about drug and alcohol program violations, including positive
            test results, refusals to test, and completion of return-to-duty and follow-up testing requirements.
          </p>
        </div>
      </>
    ),
  },
];

const ClearinghouseInfo = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [markingQuery, setMarkingQuery] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryDate, setQueryDate] = useState('');
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [consentDate, setConsentDate] = useState('');
  const [consentTo, setConsentTo] = useState('');

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/clearinghouse/status`, auth);
      setStatus(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Could not load Clearinghouse status.');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const toggleAccordion = (id) => {
    setOpenAccordion((prev) => (prev === id ? null : id));
  };

  const registrationBadge = () => {
    if (loading || !status) return { label: '…', className: 'clearinghouse-page__badge clearinghouse-page__badge--muted' };
    if (status.status === 'unknown' || status.isRegistered === null) {
      return { label: 'Status Unknown', className: 'clearinghouse-page__badge clearinghouse-page__badge--amber' };
    }
    if (status.isRegistered === true) {
      return { label: 'Registered', className: 'clearinghouse-page__badge clearinghouse-page__badge--green' };
    }
    return { label: 'Not Registered', className: 'clearinghouse-page__badge clearinghouse-page__badge--red' };
  };

  const consentBadge = () => {
    if (loading || !status) return { label: '…', className: 'clearinghouse-page__badge clearinghouse-page__badge--muted' };
    if (!status.lastConsentDate) {
      return { label: 'Not provided', className: 'clearinghouse-page__badge clearinghouse-page__badge--amber' };
    }
    if (status.consentValid) {
      return { label: 'Active', className: 'clearinghouse-page__badge clearinghouse-page__badge--green' };
    }
    return { label: 'Expired', className: 'clearinghouse-page__badge clearinghouse-page__badge--red' };
  };

  const showDriAlert =
    !loading &&
    status &&
    (status.status === 'unknown' || status.isRegistered === null || status.isRegistered === false);

  const reg = registrationBadge();
  const con = consentBadge();
  const queryLoggedToday = isLocalCalendarDate(status?.lastDriverQueryDate);

  const handleMarkQueryComplete = async () => {
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setMarkingQuery(true);
      await axios.post(`${API_URL}/api/clearinghouse/self-query-complete`, {}, auth);
      toast.success('Query date saved. Your DRI Score™ will reflect this on the next refresh.');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save query date.');
    } finally {
      setMarkingQuery(false);
    }
  };

  const toggleQueryForm = () => {
    setShowQueryForm((open) => {
      const next = !open;
      if (next) setQueryDate(toDateInputValue(status?.lastDriverQueryDate));
      return next;
    });
  };

  const handleQueryButtonClick = () => {
    if (status?.lastDriverQueryDate) {
      toggleQueryForm();
      return;
    }
    handleMarkQueryComplete();
  };

  const handleSaveQueryDate = async (e) => {
    e.preventDefault();
    if (!queryDate) {
      toast.error('Choose the query date.');
      return;
    }
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setMarkingQuery(true);
      await axios.put(
        `${API_URL}/api/clearinghouse/status`,
        { lastDriverQueryDate: queryDate },
        auth,
      );
      toast.success('Query date updated. Your DRI Score™ will reflect this on the next refresh.');
      setShowQueryForm(false);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save query date.');
    } finally {
      setMarkingQuery(false);
    }
  };

  const toggleConsentForm = () => {
    setShowConsentForm((open) => {
      const next = !open;
      if (next) {
        setConsentDate(toDateInputValue(status?.lastConsentDate));
        setConsentTo(status?.consentGivenTo || '');
      }
      return next;
    });
  };

  const handleLogConsent = async (e) => {
    e.preventDefault();
    if (!consentDate) {
      toast.error('Choose the consent date.');
      return;
    }
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setConsentSaving(true);
      await axios.put(
        `${API_URL}/api/clearinghouse/status`,
        {
          lastConsentDate: /^\d{4}-\d{2}-\d{2}$/.test(consentDate)
            ? `${consentDate}T12:00:00`
            : consentDate,
          consentGivenTo: consentTo || undefined,
        },
        auth,
      );
      toast.success('Consent date saved. A one-year expiration reminder will apply.');
      setShowConsentForm(false);
      setConsentDate('');
      setConsentTo('');
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  return (
    <div className="clearinghouse-page page-container">
      <PageHeader title="Clearinghouse" subtitle="Track your FMCSA Drug & Alcohol Clearinghouse standing" />

      <section className="clearinghouse-page__status-hero" aria-label="Your Clearinghouse status">
        <div className="clearinghouse-page__status-hero-inner">
          <div className="clearinghouse-page__status-hero-top">
            <FlaskConical size={28} className="clearinghouse-page__status-icon" aria-hidden />
            <h2 className="clearinghouse-page__status-title">Your Clearinghouse Status</h2>
          </div>
          {loading ? (
            <p className="clearinghouse-page__muted">Loading status…</p>
          ) : (
            <>
              <div className="clearinghouse-page__status-row">
                <span className="clearinghouse-page__status-label">Registration</span>
                <span className={reg.className}>{reg.label}</span>
              </div>
              <div className="clearinghouse-page__status-row">
                <span className="clearinghouse-page__status-label">Last query run</span>
                <span className="clearinghouse-page__status-value">
                  {status?.lastDriverQueryDate ? formatLongDate(status.lastDriverQueryDate) : 'No query on record'}
                </span>
              </div>
              <div className="clearinghouse-page__status-row">
                <span className="clearinghouse-page__status-label">Consent status</span>
                <span className={con.className}>{con.label}</span>
              </div>
              {status?.consentExpirationDate && status.lastConsentDate && (
                <p className="clearinghouse-page__consent-expiry">
                  Consent expiration: {formatLongDate(status.consentExpirationDate)}
                </p>
              )}
              <p className="clearinghouse-page__legal-line">
                Clearinghouse registration is required by federal law for all CDL holders.
              </p>
            </>
          )}
        </div>
      </section>

      {showDriAlert && (
        <div className="clearinghouse-page__alert-banner" role="alert">
          <AlertTriangle size={22} className="clearinghouse-page__alert-icon" aria-hidden />
          <p>
            Your Clearinghouse registration status is unconfirmed. This affects your DRI Score™.
          </p>
        </div>
      )}

      <section className="clearinghouse-page__actions" aria-label="Primary actions">
        <p
          className="clearinghouse-page__action-hint"
          style={{ gridColumn: '1 / -1', marginBottom: 0 }}
        >
          These dates are self-reported for your own compliance tracking. The actual query and
          consent are completed directly on FMCSA&apos;s Clearinghouse site (linked below).
        </p>
        <div className="clearinghouse-page__action-card card">
          <h3 className="clearinghouse-page__action-title">Run a Query</h3>
          <p className="clearinghouse-page__action-desc">
            Check your Clearinghouse record for violations. Required annually by FMCSA.
          </p>
          <a
            href={FMCSA_CLEARINGHOUSE}
            target="_blank"
            rel="noopener noreferrer"
            className="ui-btn ui-btn--primary clearinghouse-page__action-primary"
          >
            Run Query at FMCSA
            <ExternalLink size={18} />
          </a>
          <p className="clearinghouse-page__action-hint">
            After running your query, mark it complete below to update your DRI Score™
            {queryLoggedToday
              ? ' Already logged for today — you can still correct the date if this was entered by mistake.'
              : ''}
          </p>
          <button
            type="button"
            className="ui-btn ui-btn--secondary clearinghouse-page__action-secondary"
            onClick={handleQueryButtonClick}
            disabled={markingQuery && !showQueryForm}
            aria-expanded={status?.lastDriverQueryDate ? showQueryForm : undefined}
          >
            {markingQuery && !showQueryForm
              ? 'Saving…'
              : status?.lastDriverQueryDate
                ? 'Update Query Date'
                : 'Mark Query Complete'}
          </button>
          {showQueryForm && (
            <form className="clearinghouse-page__consent-form" onSubmit={handleSaveQueryDate}>
              <label className="clearinghouse-page__field">
                <span>Query date</span>
                <input
                  type="date"
                  className="clearinghouse-page__input"
                  value={queryDate}
                  onChange={(e) => setQueryDate(e.target.value)}
                  required
                />
              </label>
              <button type="submit" className="ui-btn ui-btn--primary" disabled={markingQuery}>
                {markingQuery ? 'Saving…' : 'Save query date'}
              </button>
            </form>
          )}
        </div>

        <div className="clearinghouse-page__action-card card">
          <h3 className="clearinghouse-page__action-title">Employer Consent</h3>
          <p className="clearinghouse-page__action-desc">
            Consent allows employers to query your record. Valid for 1 year.
          </p>
          {status?.lastConsentDate && (
            <p className="clearinghouse-page__action-meta">
              Last logged consent: {formatLongDate(status.lastConsentDate)}
              {status.consentGivenTo ? ` · ${status.consentGivenTo}` : ''}
            </p>
          )}
          <a
            href={FMCSA_CLEARINGHOUSE}
            target="_blank"
            rel="noopener noreferrer"
            className="ui-btn ui-btn--primary clearinghouse-page__action-primary"
          >
            Update Consent at FMCSA
            <ExternalLink size={18} />
          </a>
          <button
            type="button"
            className="ui-btn ui-btn--secondary clearinghouse-page__action-secondary"
            onClick={toggleConsentForm}
            aria-expanded={showConsentForm}
          >
            {status?.lastConsentDate ? 'Update Consent' : 'Log Consent Date'}
          </button>
          {showConsentForm && (
            <form className="clearinghouse-page__consent-form" onSubmit={handleLogConsent}>
              <label className="clearinghouse-page__field">
                <span>Consent date</span>
                <input
                  type="date"
                  className="clearinghouse-page__input"
                  value={consentDate}
                  onChange={(e) => setConsentDate(e.target.value)}
                  required
                />
              </label>
              <label className="clearinghouse-page__field">
                <span>Employer / company (optional)</span>
                <input
                  type="text"
                  className="clearinghouse-page__input"
                  value={consentTo}
                  onChange={(e) => setConsentTo(e.target.value)}
                  placeholder="Who received consent"
                />
              </label>
              <button type="submit" className="ui-btn ui-btn--primary" disabled={consentSaving}>
                {consentSaving ? 'Saving…' : 'Save consent'}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="clearinghouse-page__dri-card" aria-label="DRI Score connection">
        <h3 className="clearinghouse-page__dri-title">How It Affects Your DRI Score™</h3>
        <ul className="clearinghouse-page__dri-list">
          <li>
            Clearinghouse registration, consent, and your annual self-query are included in your{' '}
            <strong>Compliance documentation</strong> component—that is <strong>20%</strong> of your DRI Score™.
          </li>
          <li>Running your annual query on time and marking it complete in Sovrynn keeps that portion of your score current.</li>
          <li>
            Unconfirmed registration, missing or expired consent, or an annual query older than 365 days can reduce your
            Compliance documentation score by up to <strong>35 points</strong> (before weighting)—which lowers your overall
            DRI Score™. Missing or expired consent alone can account for up to <strong>12 points</strong> of that reduction
            when you are registered.
          </li>
        </ul>
      </section>

      <section className="clearinghouse-page__accordion-section" aria-label="Educational resources">
        <h3 className="clearinghouse-page__section-heading">What You Need to Know</h3>
        <div className="clearinghouse-page__accordion">
          {ACCORDION_SECTIONS.map((item) => {
            const open = openAccordion === item.id;
            return (
              <div key={item.id} className="clearinghouse-page__accordion-item">
                <button
                  type="button"
                  className="clearinghouse-page__accordion-trigger"
                  aria-expanded={open}
                  onClick={() => toggleAccordion(item.id)}
                >
                  <span className="clearinghouse-page__accordion-chevron" aria-hidden>
                    {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </span>
                  <span>{item.title}</span>
                </button>
                {open && <div className="clearinghouse-page__accordion-panel">{item.content}</div>}
              </div>
            );
          })}
        </div>
      </section>

      <p className="clearinghouse-page__footer-link-wrap">
        <a href={FMCSA_CLEARINGHOUSE} target="_blank" rel="noopener noreferrer" className="clearinghouse-page__footer-link">
          Go directly to clearinghouse.fmcsa.dot.gov
        </a>
      </p>
    </div>
  );
};

export default ClearinghouseInfo;
